import type { ImagePlaceholder } from '../types/listing';

/**
 * Relevance scoring for the preset-image picker.
 *
 * The catalog is ~55 images and the modal already holds all of them in
 * memory, so this runs locally on every keystroke rather than round-tripping
 * to the `searchText` endpoint. That keeps the picker instant and usable
 * while offline.
 *
 * Two rules do most of the work:
 *
 * 1. **AND across tokens, OR across fields.** Every word in the query must
 *    hit *something* on the image, but the words may land on different
 *    fields. "chicken soup" matches Matzo Ball Soup via a keyword and a
 *    title word — a plain substring match on the title finds nothing.
 * 2. **Field weights, so exact title hits stay on top.** Keywords are
 *    deliberately generous (they list dishes an illustration could stand in
 *    for), which would drown out real matches if everything scored equally.
 */

/**
 * `categories` is typed as required because the API always sends an array —
 * the backend normalizes NULL to `[]` on read. The `?? []` guards below are
 * for version skew only: a frontend deployed ahead of the backend talks to an
 * API that still returns the old scalar `category`, leaving `categories`
 * genuinely undefined at runtime. Without them the whole picker throws.
 */

/** Highest-scoring field wins per token; the weights encode that ordering. */
const WEIGHT_TITLE_WORD = 12;
const WEIGHT_TITLE_SUBSTRING = 8;
const WEIGHT_CATEGORY = 5;
const WEIGHT_KEYWORD_EXACT = 4;
const WEIGHT_KEYWORD_PARTIAL = 2;

export function tokenize(query: string): string[] {
  return query.toLowerCase().trim().split(/\s+/).filter(Boolean);
}

/**
 * Score one image against pre-tokenized query terms.
 * Returns 0 when any token goes unmatched — that image is not a result.
 */
export function scoreImage(img: ImagePlaceholder, tokens: string[]): number {
  const title = img.title.toLowerCase();
  const titleWords = title.split(/\s+/);
  let total = 0;

  for (const token of tokens) {
    let best = 0;

    if (titleWords.includes(token)) {
      best = WEIGHT_TITLE_WORD;
    } else if (title.includes(token)) {
      best = WEIGHT_TITLE_SUBSTRING;
    }

    if (best < WEIGHT_CATEGORY) {
      for (const category of img.categories ?? []) {
        if (category.toLowerCase().includes(token)) {
          best = WEIGHT_CATEGORY;
          break;
        }
      }
    }

    if (best < WEIGHT_KEYWORD_EXACT) {
      for (const keyword of img.keywords ?? []) {
        const k = keyword.toLowerCase();
        if (k === token) {
          best = WEIGHT_KEYWORD_EXACT;
          break;
        }
        // Prefix so "veg" reaches "vegetarian"; word-membership so a
        // single token reaches into a multi-word keyword ("chicken" ->
        // "chicken and dumplings").
        if (k.startsWith(token) || k.split(/\s+/).includes(token)) {
          best = Math.max(best, WEIGHT_KEYWORD_PARTIAL);
        }
      }
    }

    // Unmatched token — the image fails the query outright.
    if (best === 0) return 0;
    total += best;
  }

  return total;
}

/**
 * Filter by category chip, then rank by query relevance.
 * An empty query preserves the catalog's own order.
 */
export function searchImages(
  images: ImagePlaceholder[],
  query: string,
  category: string | null,
): ImagePlaceholder[] {
  const inCategory = category
    ? images.filter((img) => (img.categories ?? []).includes(category))
    : images;

  const tokens = tokenize(query);
  if (tokens.length === 0) return inCategory;

  return inCategory
    .map((img) => ({ img, score: scoreImage(img, tokens) }))
    .filter(({ score }) => score > 0)
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      // Equal scores: prefer the more general image, so a bare "panini"
      // surfaces Sandwich above Sub Sandwich.
      if (a.img.title.length !== b.img.title.length) {
        return a.img.title.length - b.img.title.length;
      }
      return a.img.title.localeCompare(b.img.title);
    })
    .map(({ img }) => img);
}
